import type { MarkerCategoryInput } from './schema';

/**
 * Marker categories offered by the marker panel.
 *
 * Categories are data, not code: adding a new one here makes it appear in the
 * panel with its own toggle, icon and colour. No component enumerates them.
 *
 * `bluemapSetId` is the link to BlueMap. BlueMap serves marker data as named
 * "marker sets"; each category points at the set it represents, and toggling the
 * category shows or hides that set. A category whose set is absent from the
 * BlueMap data simply renders as empty rather than as an error.
 *
 * `source: 'api'` marks a category that will be served by the Minecraft plugin
 * API instead. Those are listed and selectable, but report "unavailable" until
 * `PUBLIC_API_BASE_URL` is configured, they never show invented data.
 */
export const markerCategories: MarkerCategoryInput[] = [
	{
		id: 'spawn',
		name: 'Spawn',
		description: 'Spawn points and the central hub.',
		icon: 'Home',
		order: 0,
		visibleByDefault: true,
		bluemapSetId: 'spawn'
	},
	{
		id: 'warps',
		name: 'Warps',
		description: 'Public warp destinations.',
		icon: 'Signpost',
		order: 10,
		visibleByDefault: true,
		bluemapSetId: 'warps'
	},
	{
		id: 'shops',
		name: 'Shops',
		description: 'Player and admin shops.',
		icon: 'Store',
		order: 20,
		visibleByDefault: true,
		bluemapSetId: 'shops'
	},
	{
		id: 'auction',
		name: 'Auction House',
		description: 'Auction house locations.',
		icon: 'Gavel',
		order: 30,
		visibleByDefault: false,
		bluemapSetId: 'auction'
	},
	{
		id: 'crates',
		name: 'Crates',
		description: 'Crate locations and reward points.',
		icon: 'Package',
		order: 40,
		visibleByDefault: false,
		bluemapSetId: 'crates'
	},
	{
		id: 'dungeons',
		name: 'Dungeons',
		description: 'Dungeon entrances and instanced content.',
		icon: 'Skull',
		order: 50,
		visibleByDefault: false,
		bluemapSetId: 'dungeons'
	},
	{
		id: 'pvp',
		name: 'PvP',
		description: 'PvP zones and arena entrances.',
		icon: 'Swords',
		order: 60,
		visibleByDefault: false,
		bluemapSetId: 'pvp'
	},
	{
		id: 'events',
		name: 'Events',
		description: 'Active and upcoming event locations.',
		icon: 'Sparkles',
		order: 70,
		visibleByDefault: true,
		bluemapSetId: 'events'
	},
	{
		id: 'factions',
		name: 'Factions',
		description: 'Faction home points and claims.',
		icon: 'Flag',
		order: 80,
		visibleByDefault: false,
		bluemapSetId: 'factions'
	},
	{
		id: 'npcs',
		name: 'NPCs',
		description: 'Quest givers and notable NPCs.',
		icon: 'Users',
		order: 90,
		visibleByDefault: false,
		bluemapSetId: 'npcs'
	},

	/*
	 * API-backed examples. These demonstrate the plugin integration path: the UI,
	 * the toggles and the search wiring already exist, and the data arrives as soon
	 * as the plugin API is reachable. Until then they report "unavailable".
	 */
	{
		id: 'live-events',
		name: 'Live Events',
		description: 'Currently running server events, pushed by the plugin API.',
		icon: 'Radio',
		order: 100,
		visibleByDefault: true,
		source: 'api'
	},
	{
		id: 'player-claims',
		name: 'Player Claims',
		description: 'Land claims, served live by the plugin API.',
		icon: 'LandPlot',
		order: 110,
		visibleByDefault: false,
		source: 'api'
	}
];
